// src/pages/YahooVariableCostPage.tsx
//
// Yahoo!ショッピング 変動費入力ページ。Amazon版 (AmazonVariableCostPage) と同じ操作感
// （12ヶ月テーブル・一括適用・右上保存・未保存ヒント）で、項目は Yahoo 仕様:
//   - 平均原価率（%）→ avg_cost_rate（0..1 の小数で保存。原価額＝売上×この率）
//   - 物流費（月額・税込）→ logistics_cost（損益側で日割り計上）
// 保存先は user_input.ysc_monthly_costs（部分更新。固定費ページの入力は消さない）。

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ToastViewport, useToast } from "../components/Toast";
import { apiGetJson, apiPutJson } from "../utils/apiClient";

const TABLE_CLASS = "min-w-max text-xs border-collapse";
const STICKY_TH = "sticky left-0 bg-gray-100 z-10 min-w-[220px]";
const TH_CELL = "p-1 border min-w-[92px]";
const TD_CELL = "p-1 border text-center align-top";
const LEFT_TD = "sticky left-0 bg-white z-10 p-1 border text-left min-w-[220px]";
const UI_INPUT_NUM_CELL = "border rounded px-1 py-0.5 text-[11px] w-[84px] text-right";

type MonthForm = Record<string, null | number | string>;

function buildMonthKeys(year: number): string[] {
    return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
}

// avg_cost_rate は 0..1 の小数で保存、画面では % で入出力する
function rateToPercentText(v: unknown): string {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return String(Math.round(n * 10000) / 100);
}

export default function YahooVariableCostPage() {
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [dataByMonth, setDataByMonth] = useState<Record<string, MonthForm>>({});
    const [bulkLogistics, setBulkLogistics] = useState("");
    const [bulkRate, setBulkRate] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [dirtyHintDismissed, setDirtyHintDismissed] = useState(false);
    useEffect(() => {
        if (dirty) setDirtyHintDismissed(false);
    }, [dirty]);

    /** ✅ 未保存ガード：タブ閉じ/リロード時にブラウザ標準の確認を出す（楽天版と同じ） */
    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    /** ✅ 未保存ガード：年切替の前に確認（楽天版と同じ） */
    const confirmDiscardIfDirty = () => {
        if (!dirty) return true;
        return window.confirm("未保存の変更があります。\n移動すると入力内容は破棄されます。よろしいですか？");
    };
    const [error, setError] = useState<string | null>(null);
    const { toasts, pushToast, removeToast } = useToast();
    const loadTokenRef = useRef(0);

    const monthKeys = buildMonthKeys(year);

    const load = useCallback(async () => {
        const token = ++loadTokenRef.current;
        setLoading(true);
        setError(null);
        try {
            const data = await apiGetJson<{
                months: string[];
                rows: Record<string, MonthForm>;
            }>(`/api/yahoo-expense?year_month=${year}-01-01`);
            if (token !== loadTokenRef.current) return;
            setDataByMonth(data.rows ?? {});
            setDirty(false);
        } catch (e) {
            if (token === loadTokenRef.current) setError("変動費の読み込みに失敗しました。");
        } finally {
            if (token === loadTokenRef.current) setLoading(false);
        }
    }, [year]);

    useEffect(() => {
        void load();
    }, [load]);

    const handleIntChange = (ym: string, raw: string) => {
        setDataByMonth((prev) => {
            const next = { ...prev };
            if (raw === "") {
                next[ym] = { ...prev[ym], logistics_cost: null };
            } else {
                const num = Number(raw);
                if (!Number.isFinite(num) || num < 0) return prev;
                next[ym] = { ...prev[ym], logistics_cost: Math.trunc(num) };
            }
            return next;
        });
        setDirty(true);
    };

    const handleRateChange = (ym: string, raw: string) => {
        setDataByMonth((prev) => {
            const next = { ...prev };
            if (raw === "") {
                next[ym] = { ...prev[ym], avg_cost_rate: null };
            } else {
                const num = Number(raw);
                if (!Number.isFinite(num) || num < 0 || num > 100) return prev;
                next[ym] = { ...prev[ym], avg_cost_rate: num / 100 };
            }
            return next;
        });
        setDirty(true);
    };

    const applyBulk = () => {
        const logisticsNum = Number(bulkLogistics);
        const rateNum = Number(bulkRate);
        const applyLogistics = bulkLogistics !== "" && Number.isFinite(logisticsNum) && logisticsNum >= 0;
        const applyRate = bulkRate !== "" && Number.isFinite(rateNum) && rateNum >= 0 && rateNum <= 100;
        if (!applyLogistics && !applyRate) return;
        setDataByMonth((prev) => {
            const next: Record<string, MonthForm> = { ...prev };
            for (const ym of monthKeys) {
                next[ym] = {
                    ...prev[ym],
                    ...(applyLogistics ? { logistics_cost: Math.trunc(logisticsNum) } : {}),
                    ...(applyRate ? { avg_cost_rate: rateNum / 100 } : {}),
                };
            }
            return next;
        });
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const items = monthKeys.map((ym) => ({
                avg_cost_rate: dataByMonth[ym]?.avg_cost_rate ?? null,
                logistics_cost: dataByMonth[ym]?.logistics_cost ?? null,
                year_month: ym,
            }));
            await apiPutJson("/api/yahoo-expense", { items });
            pushToast("success", "12ヶ月分を保存しました");
            await load();
        } catch (e) {
            setError("保存に失敗しました。もう一度お試しください。");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-3">
            <ToastViewport toasts={toasts} onClose={removeToast}>
                {/* 未保存ヒント（閉じるまで常駐・楽天版と同じ） */}
                {dirty && !dirtyHintDismissed && !saving && (
                    <div className="pointer-events-auto">
                        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 shadow">
                            <div className="text-xs text-amber-900">変更があります。右上の「12ヶ月分を保存」を押してください。</div>
                            <button
                                className="ml-2 text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
                                onClick={() => setDirtyHintDismissed(true)}
                                aria-label="閉じる"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
            </ToastViewport>

            <div className="flex justify-between items-center flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-lg font-semibold">変動費・原価率（Yahoo!ショッピング / 12ヶ月）</div>
                    <label className="text-xs text-gray-600 ml-2">年</label>
                    <select
                        className="border px-2 py-1 rounded bg-white text-sm h-8"
                        value={year}
                        onChange={(e) => {
                            if (!confirmDiscardIfDirty()) return;
                            setYear(Number(e.target.value));
                        }}
                    >
                        {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                            <option key={y} value={y}>
                                {y}年
                            </option>
                        ))}
                    </select>
                    <button
                        className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setYear((p) => p - 1);
                        }}
                    >
                        ← 前年
                    </button>
                    <button
                        className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setYear((p) => p + 1);
                        }}
                    >
                        翌年 →
                    </button>
                </div>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || loading || !dirty}
                    className={`px-3 py-1 rounded text-xs text-white ${
                        saving || loading || !dirty ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                >
                    {saving ? "保存中..." : "12ヶ月分を保存"}
                </button>
            </div>

            {error && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">{error}</p>
            )}

            {/* Yahoo 仕様の案内 */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                Yahoo!ショッピングでは<strong>決済手数料・ポイント原資・広告費は請求明細から自動で取り込まれる</strong>ため、入力は不要です。
                ここでは<strong>平均原価率</strong>（原価 = 売上 × 原価率で計算）と、
                自動取得できない<strong>物流費（送料・梱包資材など月額）</strong>を登録します。
            </div>

            {/* 一括適用 */}
            <div className="rounded border bg-white px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-sm text-gray-600">平均原価率（%）</label>
                    <input
                        type="number"
                        className="border rounded px-2 py-1 text-sm w-24 text-right"
                        value={bulkRate}
                        onChange={(e) => setBulkRate(e.target.value)}
                        placeholder="例: 35"
                    />
                    <label className="text-sm text-gray-600 ml-4">物流費（月額・税込）</label>
                    <input
                        type="number"
                        className="border rounded px-2 py-1 text-sm w-32 text-right"
                        value={bulkLogistics}
                        onChange={(e) => setBulkLogistics(e.target.value)}
                        placeholder="例: 50000"
                    />
                    <button
                        type="button"
                        onClick={applyBulk}
                        className="px-3 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                    >
                        12ヶ月に一括適用
                    </button>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                    ※月ごとに変更したい場合は、下の表で各月セルを直接変更してください。
                </div>
            </div>

            {/* 12ヶ月テーブル */}
            <div className="overflow-x-auto border rounded relative">
                <table className={TABLE_CLASS}>
                    <thead className="bg-gray-100">
                        <tr>
                            <th className={`text-left p-1 border ${STICKY_TH}`}>科目</th>
                            {monthKeys.map((ym) => (
                                <th key={ym} className={`text-center ${TH_CELL}`}>
                                    {ym.replace("-", "/")}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className={LEFT_TD}>
                                <div className="font-medium">平均原価率（%）</div>
                                <div className="text-[10px] text-gray-500">原価 = 売上 × 原価率 で日次利益に反映されます</div>
                            </td>
                            {monthKeys.map((ym) => (
                                <td key={ym} className={TD_CELL}>
                                    <input
                                        type="number"
                                        className={UI_INPUT_NUM_CELL}
                                        value={rateToPercentText(dataByMonth[ym]?.avg_cost_rate)}
                                        onChange={(e) => handleRateChange(ym, e.target.value)}
                                    />
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td className={LEFT_TD}>
                                <div className="font-medium">物流費（月額・税込）</div>
                                <div className="text-[10px] text-gray-500">日割りで日次利益に反映されます</div>
                            </td>
                            {monthKeys.map((ym) => (
                                <td key={ym} className={TD_CELL}>
                                    <input
                                        type="number"
                                        className={UI_INPUT_NUM_CELL}
                                        value={dataByMonth[ym]?.logistics_cost ?? ""}
                                        onChange={(e) => handleIntChange(ym, e.target.value)}
                                    />
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="text-xs text-gray-500">※固定費は「設定を行う → 固定費入力」から登録してください。</div>
        </div>
    );
}
